import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "~/hooks/useAccount";
import { IPlan } from "~/types/billing";
import { IAccount, PlanStatus } from "~/types/account";
import { usePayment } from "./usePayment";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SetupIntent } from "@stripe/stripe-js";
import { toast } from "~/components/toast";
import { INVOICES, PLANS } from "~/api/billing/types";
import { STORE } from "~/api/store/types";
import { paths } from "~/router/paths";
import _debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";
import { statsigClient } from "~/helpers/statsig";
import { setPaymentGrace } from "~/helpers/paymentGrace";
import { log } from "~/helpers/datadog";

const SUBSCRIPTION_POLL_MAX_ATTEMPTS = 10;
const SUBSCRIPTION_POLL_INTERVAL_MS = 1500;

const isSubscriptionConfirmed = (account: IAccount | null, plan: IPlan) =>
  account?.active_subscription?.plan?.id === plan.id &&
  account?.active_subscription?.status === PlanStatus.ACTIVE;

export const useStripeConfirmation = (
  plan: IPlan | undefined,
  setIsLoading?: (val: boolean) => void,
  onSuccess?: () => void,
  disable_redirect_on_finish?: boolean,
  onSetupIntentSpent?: () => Promise<void>
) => {
  const [searchParams] = useSearchParams();

  // Either intent can come back from a Stripe redirect. Only the setup intent
  // was checked, so a payment-intent return rendered the full checkout as if
  // nothing had happened while the payment was being confirmed.
  const [isConfirming, setIsConfirming] = useState(
    (searchParams.has("setup_intent_client_secret") ||
      searchParams.has("payment_intent_client_secret")) &&
      searchParams.get("redirect_status") === "succeeded"
  );

  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { refetch, getAccount, account, setAccount } = useAccount();
  const { payStripe, lastActiveSubscription } = usePayment({
    planId: plan?.id as string,
    isUserShopify: false,
  });
  const client = useQueryClient();
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  const pollForConfirmedSubscription = async (plan: IPlan) => {
    for (
      let attempt = 1;
      attempt <= SUBSCRIPTION_POLL_MAX_ATTEMPTS;
      attempt++
    ) {
      const account = await getAccount();
      if (isSubscriptionConfirmed(account, plan)) return true;
      if (attempt < SUBSCRIPTION_POLL_MAX_ATTEMPTS) {
        await new Promise((resolve) =>
          setTimeout(resolve, SUBSCRIPTION_POLL_INTERVAL_MS)
        );
      }
    }
    return false;
  };

  const handleSuccess = async (plan: IPlan) => {
    try {
      statsigClient.logEvent("subscription_updated_fe", plan?.id, {
        plan_name: plan?.name,
        plan_price: String(plan?.cost_per_month),
      });
    } catch (e) {
      console.error("Statsig Log Error:", e);
    }

    localStorage.removeItem("plansModal");
    setPaymentGrace();

    // Immediately update account context with the subscription returned by
    // the StripeSubscribe API so the UI (e.g. plan cards, paywall gates)
    // reflects the new state without waiting for polling / webhook.
    if (lastActiveSubscription.current && account) {
      setAccount({
        ...account,
        active_subscription: lastActiveSubscription.current,
        subscription_lock_reason: null,
      });
    }

    client.invalidateQueries(PLANS);
    client.invalidateQueries(STORE);
    client.invalidateQueries(INVOICES);

    try {
      const confirmed = await pollForConfirmedSubscription(plan);
      if (!confirmed) {
        log.warn("Subscription not confirmed after post-payment polling", {
          plan_id: plan?.id,
        });
      }
    } catch (e) {
      log.warn("Post-payment subscription poll failed", { plan_id: plan?.id });
    }

    onSuccess?.();
    if (disable_redirect_on_finish) return;
    navigate(`${paths.app.home}?plan_id=${plan?.id}`);
  };

  const commitPayment = async (
    setupIntent: SetupIntent | null = null,
    promoCodeId?: string,
    returnError: boolean = false
  ) => {
    if (!plan) return;

    const error = await payStripe(
      setupIntent
        ? { setup_intent: setupIntent.id, promo_code_id: promoCodeId }
        : { promo_code_id: promoCodeId }
    );

    if (error) {
      if (
        error != "error" &&
        error.type === "invalid_request_error" &&
        error.code === "payment_intent_incompatible_payment_method"
      ) {
        refetch();
      }
      if (returnError) return { error: true };
    } else {
      await handleSuccess(plan);
    }
  };

  const debouncedSetupIntentCheck = useCallback(
    _debounce((setupIntentClientSecret: string, promoCodeId: string | null) => {
      if (!stripe || !plan) return;
      stripe
        .retrieveSetupIntent(setupIntentClientSecret)
        .then(async ({ setupIntent, error }) => {
          if (!setupIntent) {
            toast.warning(error.message ?? t("checkout.went_wrong"));
            return;
          }
          switch (setupIntent.status) {
            case "succeeded":
              if (setIsConfirming) setIsConfirming(true);
              await commitPayment(setupIntent, promoCodeId ?? undefined);
              if (setIsConfirming) setIsConfirming(false);
              break;
            case "processing":
              toast.warning(t("checkout.processing_payment"));
              break;
            case "requires_payment_method":
              toast.error(t("checkout.unsuccessful_payment"));
              navigate(window.location.pathname);
              break;
            default:
              toast.warning(t("checkout.went_wrong"));
              navigate(window.location.pathname);
              break;
          }
        });
    }, 500),
    [stripe, plan]
  );

  const debouncedPaymentIntentCheck = useCallback(
    _debounce((setupIntentClientSecret: string) => {
      if (!stripe || !plan) return;
      setIsConfirming(true);
      stripe
        .retrievePaymentIntent(setupIntentClientSecret)
        .then(async ({ paymentIntent, error }) => {
          if (!paymentIntent) {
            toast.warning(error.message ?? t("checkout.went_wrong"));
            setIsConfirming(false);
            return;
          }
          switch (paymentIntent.status) {
            case "succeeded":
              await handleSuccess(plan);
              break;
            case "processing":
              toast.warning(t("checkout.processing_payment"));
              break;
            case "requires_payment_method":
              toast.error(t("checkout.unsuccessful_payment"));
              navigate(window.location.pathname);
              break;
            default:
              toast.warning(t("checkout.went_wrong"));
              navigate(window.location.pathname);
              break;
          }
          setIsConfirming(false);
        });
    }, 500),
    [stripe, plan]
  );

  useEffect(() => {
    const setupIntentClientSecret = searchParams.get(
      "setup_intent_client_secret"
    );
    const paymentIntentClientSecret = searchParams.get(
      "payment_intent_client_secret"
    );
    const promoCodeId = searchParams.get("promo_code_id");
    if (!stripe || !plan) return;
    if (setupIntentClientSecret && !paymentIntentClientSecret)
      debouncedSetupIntentCheck(setupIntentClientSecret, promoCodeId);
    if (paymentIntentClientSecret)
      debouncedPaymentIntentCheck(paymentIntentClientSecret);
  }, [stripe, plan]);

  const handlePayment = async (returnError = false) => {
    if (!stripe || !elements || !setIsLoading || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set("plan_id", plan?.id as string);
      let returnURL = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
      const promoCodeId = searchParams.get("promo_code_id");

      try {
        // No Payment Element mounted means the user is paying with their
        // saved card (checkout shows "**** 1234", the annual plans modal has
        // no form), so there is no new card to confirm. Commit straight to
        // the backend, which charges the saved payment method.
        if (!elements.getElement("payment")) {
          const paymentError = await commitPayment(
            null,
            promoCodeId ?? undefined,
            returnError
          );
          if (returnError && paymentError?.error) return { error: true };
          return;
        }

        // `redirect: "if_required"` keeps the promise alive for card flows:
        // Stripe runs 3DS inline and resolves here instead of navigating away.
        // Without it this call only ever returns on failure, so success used
        // to land in the catch below and commit the payment without the new
        // card.
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: returnURL,
          },
          redirect: "if_required",
        });

        if (error) {
          toast.error(error.message ?? t("checkout.went_wrong"));
          if (returnError) return { error: true };
          return;
        }

        // Redirect-based methods navigate away before resolving; anything
        // still mid-authentication is picked up by the return_url effect
        // above. Only a succeeded intent is safe to commit.
        if (setupIntent?.status !== "succeeded") {
          if (setupIntent?.status === "requires_payment_method") {
            toast.error(t("checkout.unsuccessful_payment"));
            if (returnError) return { error: true };
          }
          return;
        }

        const paymentError = await commitPayment(
          setupIntent,
          promoCodeId ?? undefined,
          true
        );
        if (paymentError?.error) {
          // The card was saved but the payment failed (e.g. a decline). This
          // SetupIntent has succeeded and Stripe will not confirm it again, so
          // a retry with another card on this page needs a fresh one. Awaited
          // so the pay button stays disabled until the new form is ready.
          try {
            await onSetupIntentSpent?.();
          } catch {
            toast.warning(t("checkout.went_wrong"));
          }
          if (returnError) return { error: true };
        }
      } catch {
        toast.error(t("checkout.went_wrong"));
        if (returnError) return { error: true };
      }
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return { handlePayment, isConfirming };
};
